import { useEffect, useState } from "react";
import { ExternalLink, FileText, Link2, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DisplayDocument } from "@/components/session/DisplayDocument";
import { PdfDocument } from "@/components/session/PdfDocument";
import { useBubbleSize } from "@/hooks/useBubbleSize";
import type { SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import type { ScenarioDisplayLayout } from "@/lib/powtoon-be/types";
import type { DisplaySelection } from "./LearnerDisplayPicker";
import { cn } from "@/lib/utils";
import { BUBBLE_SIZE_CLASS } from "@/components/session/bubble";
import { UrlEmbedStatus, useUrlEmbedCheck } from "./UrlEmbedStatus";

function BlockedUrlCard({ url }: { url: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6">
      <div className="flex flex-col items-center gap-3 text-center max-w-sm">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          This page can't be embedded here.
        </p>
        <p className="text-xs text-muted-foreground break-all">{url}</p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open in a new tab
        </a>
      </div>
    </div>
  );
}

function DraftPreviewCard({ draft }: { draft: SourceInputDraft }) {
  if (draft.kind === "upload") {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground truncate max-w-[280px]">
            {draft.file_name}
          </p>
          <p className="text-xs text-muted-foreground">
            Office documents preview after saving the scenario.
          </p>
        </div>
      </div>
    );
  }
  if (draft.kind === "url") {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <Link2 className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground break-all max-w-[320px]">{draft.url}</p>
        </div>
      </div>
    );
  }
  return null;
}

function inferDraftKind(
  name: string,
  mime?: string,
): "image" | "video" | "pdf" | "office" | "other" {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m === "application/pdf") return "pdf";
  if (
    m.includes("officedocument") ||
    m === "application/vnd.ms-powerpoint" ||
    m === "application/msword" ||
    m === "application/vnd.ms-excel"
  )
    return "office";
  const n = name.toLowerCase();
  if (n.match(/\.(png|jpe?g|gif|webp|svg|bmp)$/)) return "image";
  if (n.match(/\.(mp4|webm|ogg|mov)$/)) return "video";
  if (n.endsWith(".pdf")) return "pdf";
  if (n.match(/\.(pptx?|docx?|xlsx?)$/)) return "office";
  return "other";
}

function LocalFilePreview({
  draft,
  open,
}: {
  draft: Extract<SourceInputDraft, { kind: "upload" }>;
  open: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    let u: string | null = null;
    setBlocked(false);
    if (!open || !draft._file) {
      setUrl(null);
      return;
    }
    u = URL.createObjectURL(draft._file);
    setUrl(u);
    return () => {
      if (u) URL.revokeObjectURL(u);
      setUrl(null);
    };
  }, [draft._file, open, draft._draftId]);

  const kind = inferDraftKind(draft.file_name, draft.mime);
  if (!url || kind === "office" || kind === "other") {
    return <DraftPreviewCard draft={draft} />;
  }
  if (kind === "image") {
    return (
      <div className="h-full w-full bg-background flex items-center justify-center overflow-auto">
        <img src={url} alt="" className="max-h-full max-w-full object-contain" />
      </div>
    );
  }
  if (kind === "video") {
    return (
      <div className="h-full w-full bg-black flex items-center justify-center">
        <video src={url} controls className="max-h-full max-w-full" />
      </div>
    );
  }
  // pdf
  if (blocked) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            PDF preview unavailable in this browser.
          </p>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in a new tab
          </a>
        </div>
      </div>
    );
  }
  // Same reader the learner session uses, so the trainer previews the real
  // thing (page controls included) instead of the browser's built-in viewer.
  return <PdfDocument url={url} className="bg-background" onFailure={() => setBlocked(true)} />;
}

function AvatarStandIn({ thumbUrl, size }: { thumbUrl?: string; size: "full" | "bubble" }) {
  // Fill the container on both axes, crop overflow, keep subject centered.
  // Bubble anchors to top so faces stay visible in the tall 3:4 shape.
  const cls =
    size === "bubble"
      ? "h-full w-full object-cover object-top"
      : "h-full w-full object-cover object-center";
  return (
    <div className="relative h-full w-full bg-accent flex items-center justify-center overflow-hidden">
      {thumbUrl ? (
        <img src={thumbUrl} alt="" className={cls} />
      ) : (
        <span className="text-xs text-muted-foreground">Avatar preview</span>
      )}
    </div>
  );
}

export function LearnerDisplayPreviewModal({
  open,
  onOpenChange,
  layout,
  avatarPosition,
  selection,
  sources,
  scenarioId,
  avatarThumbUrl,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  layout: ScenarioDisplayLayout;
  avatarPosition: "left" | "right";
  selection?: DisplaySelection;
  sources: SourceInputDraft[];
  scenarioId?: string;
  avatarThumbUrl?: string;
}) {
  const embedTarget = selection?.kind === "url" ? selection.url : undefined;
  const embed = useUrlEmbedCheck(open ? embedTarget : undefined);
  const urlBlocked =
    embed.result?.verdict === "blocked" || embed.result?.verdict === "unreachable";

  // Resolve the selection to a renderable preview.
  let docNode: React.ReactNode = (
    <div className="flex h-full w-full items-center justify-center bg-muted/30">
      <p className="text-xs text-muted-foreground">No document selected.</p>
    </div>
  );
  const selectedDraft =
    selection?.kind === "url"
      ? sources.find((s) => s.kind === "url" && s.url.trim() === selection.url)
      : selection?.kind === "upload"
        ? sources.find(
            (s): s is Extract<SourceInputDraft, { kind: "upload" }> =>
              s.kind === "upload" && s._draftId === selection.draftId,
          )
        : undefined;

  if (selection?.kind === "url") {
    docNode = urlBlocked ? (
      <BlockedUrlCard url={selection.url} />
    ) : (
      <DisplayDocument scenarioId={scenarioId ?? ""} source={{ kind: "url", url: selection.url }} />
    );
  } else if (selectedDraft?.kind === "upload") {
    if (selectedDraft._file) {
      docNode = <LocalFilePreview draft={selectedDraft} open={open} />;
    } else if (selectedDraft.object_id && scenarioId) {
      docNode = (
        <DisplayDocument
          scenarioId={scenarioId}
          source={{
            kind: "upload",
            object_id: selectedDraft.object_id,
            file_name: selectedDraft.file_name,
            mime: selectedDraft.mime,
          }}
        />
      );
    } else {
      docNode = <DraftPreviewCard draft={selectedDraft} />;
    }
  }

  const avatarOnLeft = avatarPosition === "left";
  const {
    containerRef: bubbleContainerRef,
    size: bubbleSize,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  } = useBubbleSize();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-[min(1180px,calc(100vw-2rem))] p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>Learner display preview</DialogTitle>
        </DialogHeader>
        <div className="p-4">
          {embedTarget && (
            <UrlEmbedStatus
              loading={embed.loading}
              result={embed.result}
              onRecheck={embed.recheck}
              className="mb-3"
            />
          )}
          <div className="relative mx-auto h-[min(680px,calc(100dvh-11rem))] w-full rounded-md border bg-background overflow-hidden">
            {layout === "side-by-side" ? (
              <div className={cn("absolute inset-0 flex", avatarOnLeft && "flex-row-reverse")}>
                <div
                  className={cn(
                    "w-2/3 h-full",
                    avatarOnLeft ? "border-l border-border" : "border-r border-border",
                  )}
                >
                  {docNode}
                </div>
                <div className="w-1/3 h-full relative">
                  <AvatarStandIn thumbUrl={avatarThumbUrl} size="full" />
                </div>
              </div>
            ) : (
              <div ref={bubbleContainerRef} className="absolute inset-0">
                <div className="absolute inset-0">{docNode}</div>
                <div
                  className={
                    "absolute top-4 " +
                    BUBBLE_SIZE_CLASS +
                    " rounded-2xl overflow-hidden border-2 border-background shadow-2xl bg-background " +
                    (avatarOnLeft ? "left-4" : "right-4")
                  }
                  style={
                    bubbleSize.width > 0
                      ? { width: bubbleSize.width, height: bubbleSize.height }
                      : undefined
                  }
                >
                  <AvatarStandIn thumbUrl={avatarThumbUrl} size="bubble" />
                  <button
                    type="button"
                    aria-label="Resize bubble"
                    className="absolute bottom-1 right-1 z-30 p-1 rounded-full bg-black/40 text-white hover:bg-black/60 cursor-nwse-resize touch-none"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      onResizeStart(e);
                    }}
                    onPointerMove={onResizeMove}
                    onPointerUp={onResizeEnd}
                    onPointerCancel={onResizeEnd}
                  >
                    <Maximize2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Approximate preview — the actual session shows the live avatar video.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
